import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Star, Shield, Clock, MapPin, ArrowRight, Shovel, Trees, Sparkles, Heart, Zap, Award, ThumbsUp, Users, Leaf, Sun, Home as HomeIcon } from 'lucide-react';
import useSiteImages from '../hooks/useSiteImages';
import { trackPhoneClick, trackWhatsAppClick, trackCTAClick } from '../hooks/useAnalytics';
import './Home.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for dynamic trust badges
const ICON_MAP = {
  Shield, CheckCircle, Clock, Sparkles, Star, Heart, Zap, Award, ThumbsUp, Users, Leaf, Sun, Home: HomeIcon
};

// Default hero content
const DEFAULT_HERO_CONTENT = {
  main_title: "Endlich wieder Freude am Garten.",
  slogan: "Lehnen Sie sich zurück.",
  subtitle: "Gartenpflege, Baumarbeiten und mehr - zuverlässig und fair in Nörten-Hardenberg, Göttingen und der ganzen Region.",
  note: "Oft Termine innerhalb weniger Tage möglich.",
  trust_badges: [
    { id: "1", icon: "Shield", text: "Haftpflichtversichert" },
    { id: "2", icon: "CheckCircle", text: "Transparente Preise" },
    { id: "3", icon: "Clock", text: "Schnelle Termine" },
    { id: "4", icon: "Sparkles", text: "Saubere Arbeit" }
  ]
};

// Default images for the two main service areas (fallback)
const DEFAULT_ERDARBEITEN_IMAGE = 'https://customer-assets.emergentagent.com/job_2fdbb258-2fe3-4bcb-af8e-a32c1bf06202/artifacts/xuzlwsfh_WhatsApp%20Image%202026-03-21%20at%2015.32.59.jpeg';
const DEFAULT_GARTENPFLEGE_IMAGE = 'https://customer-assets.emergentagent.com/job_2fdbb258-2fe3-4bcb-af8e-a32c1bf06202/artifacts/mg3au26l_WhatsApp%20Image%202026-03-21%20at%2015.53.49.jpeg';

// Fallback stock images for projects
const STOCK_IMAGES = [
  'https://images.pexels.com/photos/24595771/pexels-photo-24595771.jpeg?auto=compress&cs=tinysrgb&w=600',
  'https://images.pexels.com/photos/6728919/pexels-photo-6728919.jpeg?auto=compress&cs=tinysrgb&w=600',
  'https://images.pexels.com/photos/31127615/pexels-photo-31127615.jpeg?auto=compress&cs=tinysrgb&w=600',
  'https://images.pexels.com/photos/28348584/pexels-photo-28348584.jpeg?auto=compress&cs=tinysrgb&w=600'
];

// Fallback projects
const FALLBACK_PROJECTS = [
  { location: 'Bovenden', title: 'Heckenrückschnitt durchgeführt', description: 'Die Thujahecke war stark gewachsen und musste eingekürzt werden.', daysAgo: 2, image: STOCK_IMAGES[0] },
  { location: 'Göttingen', title: 'Aushub für Gartenhaus', description: 'Fundament vorbereitet mit Minibagger. Saubere und schnelle Arbeit.', daysAgo: 3, image: STOCK_IMAGES[1] },
  { location: 'Nörten-Hardenberg', title: 'Geländemodellierung', description: 'Grundstück planiert und für Rasenneuanlage vorbereitet.', daysAgo: 4, image: STOCK_IMAGES[2] },
  { location: 'Northeim', title: 'Regelmäßige Rasenpflege', description: 'Wir kommen alle zwei Wochen zum Mähen und Heckenschneiden.', daysAgo: 5, image: STOCK_IMAGES[3] }
];

// Format date to "vor X Tagen" format
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return 'heute';
  if (diffDays === 1) return 'gestern';
  if (diffDays < 7) return `vor ${diffDays} Tagen`;
  if (diffDays === 7) return 'vor einer Woche';
  if (diffDays < 30) return `vor ${Math.floor(diffDays / 7)} Wochen`;
  return `vor ${Math.floor(diffDays / 30)} Monaten`;
};

// Get image URL from post
const getProjectImage = (post, index) => {
  if (post.images && post.images.length > 0) {
    const imageUrl = post.images[0].url;
    if (imageUrl.startsWith('http')) return imageUrl;
    return `${BACKEND_URL}${imageUrl}`;
  }
  return STOCK_IMAGES[index % STOCK_IMAGES.length];
};

// Convert position to CSS object-position
const getPositionStyle = (position) => {
  switch (position) {
    case 'top': return 'center 30%';
    case 'bottom': return 'center 70%';
    default: return 'center 50%';
  }
};

const Home = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [reviewsData, setReviewsData] = useState({ average_rating: 5.0, total_reviews: 0 });
  const [heroContent, setHeroContent] = useState(DEFAULT_HERO_CONTENT);
  const [homepageImages, setHomepageImages] = useState({
    erdarbeiten: { image_url: DEFAULT_ERDARBEITEN_IMAGE, position: 'center' },
    gartenpflege: { image_url: DEFAULT_GARTENPFLEGE_IMAGE, position: 'bottom' },
    hero: { image_url: '', position: 'center' }
  });
  const { getImage } = useSiteImages();

  useEffect(() => {
    const fetchHeroContent = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/hero-content`);
        if (response.ok) {
          const data = await response.json();
          setHeroContent({
            main_title: data.main_title || DEFAULT_HERO_CONTENT.main_title,
            slogan: data.slogan || DEFAULT_HERO_CONTENT.slogan,
            subtitle: data.subtitle || DEFAULT_HERO_CONTENT.subtitle,
            note: data.note || DEFAULT_HERO_CONTENT.note,
            trust_badges: data.trust_badges || DEFAULT_HERO_CONTENT.trust_badges
          });
        }
      } catch (error) {
        console.error('Error fetching hero content:', error);
      }
    };

    const fetchHomepageImages = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/homepage-images`);
        if (response.ok) {
          const data = await response.json();
          setHomepageImages(prev => ({
            ...prev,
            erdarbeiten: data.erdarbeiten || prev.erdarbeiten,
            gartenpflege: data.gartenpflege || prev.gartenpflege,
            hero: data.hero || prev.hero
          }));
        }
      } catch (error) {
        console.error('Error fetching homepage images:', error);
      }
    };

    const fetchProjects = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/posts?limit=4`);
        if (response.ok) {
          const data = await response.json();
          if (data.posts && data.posts.length > 0) {
            const transformedPosts = data.posts.map((post, index) => ({
              id: post.id,
              slug: post.slug,
              location: post.location || 'Göttingen',
              title: post.title,
              description: post.excerpt || post.content?.substring(0, 150) || '',
              image: getProjectImage(post, index),
              createdAt: post.created_at,
              isReal: true
            }));
            if (transformedPosts.length < 4) {
              const neededFallbacks = 4 - transformedPosts.length;
              setProjects([...transformedPosts, ...FALLBACK_PROJECTS.slice(0, neededFallbacks)]);
            } else {
              setProjects(transformedPosts);
            }
          } else {
            setProjects(FALLBACK_PROJECTS);
          }
        } else {
          setProjects(FALLBACK_PROJECTS);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
        setProjects(FALLBACK_PROJECTS);
      } finally {
        setLoading(false);
      }
    };

    const fetchReviews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/reviews`);
        if (response.ok) {
          const data = await response.json();
          // Filter: nur 4+ Sterne, sortiert nach Datum (neueste zuerst)
          const filteredReviews = (data.reviews || [])
            .filter(review => review.rating >= 4)
            .sort((a, b) => new Date(b.time) - new Date(a.time));
          setReviews(filteredReviews);
          setReviewsData({
            average_rating: data.average_rating || 5.0,
            total_reviews: data.total_reviews || data.reviews?.length || 0
          });
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      }
    };

    fetchHeroContent();
    fetchHomepageImages();
    fetchProjects();
    fetchReviews();
  }, []);

  return (
    <div className="home-page">
      {/* ==================== HERO SECTION ==================== */}
      <section 
        className="hero-section hero-compact"
        style={homepageImages.hero?.image_url ? {
          backgroundImage: `url(${homepageImages.hero.image_url})`,
          backgroundPosition: homepageImages.hero.position || 'center',
          backgroundSize: 'cover'
        } : {}}
      >
        <div className="hero-overlay"></div>
        <div className="hero-content container">
          <h1 className="hero-main-title">{heroContent.main_title}</h1>
          <p className="hero-slogan">{heroContent.slogan}</p>
          <p className="hero-main-subtitle">{heroContent.subtitle}</p>
          
          <div className="trust-badges-inline">
            {heroContent.trust_badges.map((badge) => {
              const IconComponent = ICON_MAP[badge.icon] || Shield;
              return (
                <div key={badge.id} className="trust-badge-small">
                  <IconComponent size={16} />
                  <span>{badge.text}</span>
                </div>
              );
            })}
          </div>
          
          <div className="hero-buttons">
            <a href="/kontakt" className="btn-cta-primary">Kostenloses Angebot sichern</a>
            <a href="tel:+4917655669090" className="btn-cta-secondary">
              <Phone size={18} />
              Jetzt anrufen
            </a>
          </div>
          <p className="hero-note">{heroContent.note}</p>
        </div>
      </section>

      {/* ==================== ZWEI HAUPTBEREICHE 50/50 ==================== */}
      <section className="main-services-section">
        <div className="container">
          <div className="main-services-grid">
            
            {/* ERDARBEITEN & GARTENBAU */}
            <div className="main-service-block erdarbeiten">
              <div className="main-service-image">
                <img 
                  src={homepageImages.erdarbeiten.image_url} 
                  alt="Erdarbeiten mit Minibagger" 
                  loading="lazy"
                  style={{ objectPosition: getPositionStyle(homepageImages.erdarbeiten.position) }}
                />
                <div className="main-service-icon">
                  <Shovel size={32} />
                </div>
              </div>
              <div className="main-service-content">
                <h2>Erdarbeiten & Gartenbau</h2>
                <p className="main-service-intro">
                  Wir übernehmen Erdarbeiten und kleinere Bauprojekte rund um Haus und Garten.
                  Mit Minibagger und Kipper führen wir Aushub, Geländevorbereitung und Bauarbeiten zuverlässig und sauber aus.
                </p>
                <ul className="main-service-list">
                  <li><CheckCircle size={16} /> Baggerarbeiten & Erdarbeiten</li>
                  <li><CheckCircle size={16} /> Aushub / Auskoffern</li>
                  <li><CheckCircle size={16} /> Baugruben (Gartenhaus, Pool etc.)</li>
                  <li><CheckCircle size={16} /> Geländevorbereitung / Planieren</li>
                  <li><CheckCircle size={16} /> Boden abtragen & auffüllen</li>
                  <li><CheckCircle size={16} /> Erdtransport & Entsorgung</li>
                  <li><CheckCircle size={16} /> Winkelstützen setzen</li>
                  <li><CheckCircle size={16} /> Kleine Mauern (Schalstein)</li>
                  <li><CheckCircle size={16} /> Hangabfangungen</li>
                  <li><CheckCircle size={16} /> Vorbereitung für Pflasterflächen</li>
                  <li><CheckCircle size={16} /> Drainage / Versickerung</li>
                </ul>
                <Link to="/leistungen/erdarbeiten" className="main-service-btn main-service-btn--erdarbeiten">
                  Erdarbeiten ansehen
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            {/* GARTENPFLEGE */}
            <div className="main-service-block gartenpflege">
              <div className="main-service-image">
                <img 
                  src={homepageImages.gartenpflege.image_url} 
                  alt="Professionelle Gartenpflege" 
                  loading="lazy"
                  style={{ objectPosition: getPositionStyle(homepageImages.gartenpflege.position) }}
                />
                <div className="main-service-icon">
                  <Trees size={32} />
                </div>
              </div>
              <div className="main-service-content">
                <h2>Gartenpflege</h2>
                <p className="main-service-intro">
                  Wir kümmern uns zuverlässig um die Pflege Ihres Gartens.
                  Von regelmäßigem Rasenmähen bis hin zum Hecken- und Baumschnitt sorgen wir für ein gepflegtes Gesamtbild.
                </p>
                <ul className="main-service-list">
                  <li><CheckCircle size={16} /> Rasen mähen</li>
                  <li><CheckCircle size={16} /> Hecken schneiden</li>
                  <li><CheckCircle size={16} /> Baumschnitt (kleinere Bäume)</li>
                  <li><CheckCircle size={16} /> Gartenpflege allgemein</li>
                  <li><CheckCircle size={16} /> Rasenneuanlage</li>
                  <li><CheckCircle size={16} /> Rasensanierung</li>
                  <li><CheckCircle size={16} /> Laubarbeiten</li>
                  <li><CheckCircle size={16} /> Grünschnittentsorgung</li>
                </ul>
                <Link to="/leistungen/gartenpflege" className="main-service-btn main-service-btn--gartenpflege">
                  Gartenpflege ansehen
                  <ArrowRight size={18} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CTA BANNER MIT ZUSATZLEISTUNGEN ==================== */}
      <section className="cta-banner-section">
        <div className="container">
          {/* Zusatzleistungen als Teil des CTA-Bereichs */}
          <div className="additional-services-inline">
            <span className="additional-label">Weitere Leistungen:</span>
            <div className="additional-tags">
              <span className="additional-tag"><CheckCircle size={14} /> Baumstumpf fräsen</span>
              <span className="additional-tag"><CheckCircle size={14} /> Rückbau / Abbruch</span>
              <span className="additional-tag"><CheckCircle size={14} /> Entsorgung</span>
              <span className="additional-tag"><CheckCircle size={14} /> Hochdruckreinigung</span>
            </div>
          </div>
          
          <div className="cta-banner">
            <div className="cta-banner-content">
              <h2>Projekt besprechen?</h2>
              <p>Rufen Sie uns an oder schreiben Sie uns. Wir melden uns schnell zurück.</p>
            </div>
            <div className="cta-banner-actions">
              <a href="tel:+4917655669090" className="btn-cta">
                <Phone size={18} />
                0176 - 556 690 90
              </a>
              <Link to="/kontakt" className="btn-secondary">
                Kontaktformular
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== AKTUELLE PROJEKTE ==================== */}
      <section className="local-projects-section">
        <div className="container">
          <div className="section-header">
            <h2>Aktuelle Projekte</h2>
            <p>Was wir zuletzt in der Region erledigt haben.</p>
          </div>
          
          <div className="local-projects-grid projects-4">
            {projects.slice(0, 4).map((project, index) => (
              <div key={project.id || index} className="local-project-card" data-testid={`local-project-${index}`}>
                {project.isReal && project.slug ? (
                  <Link to={`/projekte/${project.slug}`} className="local-project-link">
                    <div className="local-project-image">
                      <img src={project.image} alt={project.title} loading="lazy" />
                      <div className="local-project-location">
                        <MapPin size={16} />
                        <span>{project.location}</span>
                      </div>
                    </div>
                    <div className="local-project-content">
                      <h3 className="local-project-title">{project.title}</h3>
                      <p className="local-project-desc">{project.description}</p>
                      <span className="local-project-time">
                        {project.createdAt ? formatTimeAgo(project.createdAt) : `vor ${project.daysAgo} Tagen`}
                      </span>
                    </div>
                  </Link>
                ) : (
                  <>
                    <div className="local-project-image">
                      <img src={project.image} alt={project.title} loading="lazy" />
                      <div className="local-project-location">
                        <MapPin size={16} />
                        <span>{project.location}</span>
                      </div>
                    </div>
                    <div className="local-project-content">
                      <h3 className="local-project-title">{project.title}</h3>
                      <p className="local-project-desc">{project.description}</p>
                      <span className="local-project-time">vor {project.daysAgo} Tagen</span>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
          
          <div className="local-projects-cta">
            <Link to="/projekte" className="btn-secondary-outline">
              Alle Projekte ansehen
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== WARUM WIR ==================== */}
      <section className="why-us-section">
        <div className="container">
          <div className="section-header">
            <h2>Warum Kunden uns vertrauen</h2>
            <p>Qualität und Zuverlässigkeit aus der Region</p>
          </div>
          <div className="why-us-grid">
            <div className="why-us-item">
              <div className="why-us-icon"><Shield size={26} /></div>
              <h3>Zuverlässig</h3>
              <p>Wir halten Termine ein und sind erreichbar, wenn Sie uns brauchen.</p>
            </div>
            <div className="why-us-item">
              <div className="why-us-icon"><CheckCircle size={26} /></div>
              <h3>Faire Preise</h3>
              <p>Sie wissen vorher, was es kostet. Keine versteckten Kosten.</p>
            </div>
            <div className="why-us-item">
              <div className="why-us-icon"><Star size={26} /></div>
              <h3>Persönlich</h3>
              <p>Direkt vom Chef. Wir schauen gemeinsam, was sinnvoll ist.</p>
            </div>
            <div className="why-us-item">
              <div className="why-us-icon"><MapPin size={26} /></div>
              <h3>Regional</h3>
              <p>Wir kennen die Region und sind schnell bei Ihnen vor Ort.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== GOOGLE REVIEWS ==================== */}
      {reviews.length > 0 && (
        <section className="reviews-section">
          <div className="container">
            <div className="reviews-header-box">
              <img src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" alt="Google" className="google-logo" />
              <div className="reviews-rating-info">
                <div className="stars-row">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={22} fill="#FBBC04" color="#FBBC04" />
                  ))}
                </div>
                <span className="rating-text">{reviewsData.average_rating.toFixed(1)} von 5 • {reviewsData.total_reviews} Bewertungen</span>
              </div>
            </div>
            
            <div className="reviews-grid">
              {reviews.slice(0, 3).map((review, index) => (
                <div key={index} className="review-card" data-testid={`review-card-${index}`}>
                  <div className="review-card-header">
                    {review.author_photo ? (
                      <img src={review.author_photo} alt={review.author} className="author-photo" />
                    ) : (
                      <div className="author-initial">{review.author?.[0] || 'K'}</div>
                    )}
                    <div className="review-stars-inline">
                      {[...Array(review.rating || 5)].map((_, i) => (
                        <Star key={i} size={16} fill="#FBBC04" color="#FBBC04" />
                      ))}
                    </div>
                  </div>
                  <p className="review-text">{review.text}</p>
                  <div className="review-footer">
                    <span className="author-name">{review.author}</span>
                    <span className="review-date">{review.relative_time}</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="reviews-cta">
              <Link to="/bewertungen" className="btn-secondary-outline">
                Alle Bewertungen lesen
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* ==================== SERVICE AREA ==================== */}
      <section className="service-area-section">
        <div className="container">
          <div className="service-area-content">
            <h2>Immer in Ihrer Nähe</h2>
            <p>Wir sind in Göttingen, Nörten-Hardenberg, Northeim, Bovenden, Einbeck und der gesamten Region für Sie unterwegs.</p>
            <div className="service-area-links-home">
              {[
                ['/gartenpflege/goettingen', 'Gartenpflege Göttingen'],
                ['/erdarbeiten/goettingen', 'Erdarbeiten Göttingen'],
                ['/baumfaellung/goettingen', 'Baumfällung Göttingen'],
                ['/gartenpflege/northeim', 'Gartenpflege Northeim'],
                ['/erdarbeiten/northeim', 'Erdarbeiten Northeim'],
                ['/gartenpflege/duderstadt', 'Gartenpflege Duderstadt'],
                ['/erdarbeiten/einbeck', 'Erdarbeiten Einbeck'],
                ['/baumfaellung/northeim', 'Baumfällung Northeim'],
                ['/hochdruckreinigung/goettingen', 'Hochdruckreinigung Göttingen'],
                ['/gartenpflege/bovenden', 'Gartenpflege Bovenden'],
              ].map(([path, label]) => (
                <Link key={path} to={path} className="service-area-link-tag">{label}</Link>
              ))}
            </div>
            <div className="service-area-cta">
              <Link to="/kontakt" className="btn-cta">
                Jetzt anfragen
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== MOBILE CTA BAR ==================== */}
      <div className="mobile-cta-bar">
        <a href="tel:+4917655669090" className="mobile-cta-btn call" onClick={() => trackPhoneClick()}>
          <Phone size={20} />
          <span>Anrufen</span>
        </a>
        <Link to="/kontakt" className="mobile-cta-btn quote" onClick={() => trackCTAClick('mobile_cta_quote')}>
          <span>Angebot sichern</span>
        </Link>
      </div>

      {/* WhatsApp Button */}
      <a 
        href="https://wa.me/4917655669090" 
        className="whatsapp-float"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp kontaktieren"
        onClick={() => trackWhatsAppClick()}
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default Home;
